import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Select from '@atoms/Select/Select';
import Text from '@atoms/Text/Text';
import SizeEnum from '@models/enums/SizeEnum';
import {
  ILocationListing,
  ILocationListingRequest,
  ILocationListingResponse,
} from '@models/ILocationListing';
import ContactCard from '@molecules/ContactCard/ContactCard';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

const LocationListing = ({
  dropdownLabel,
  countryList,
  header,
  subHeader,
  apiEndpoint,
  pageLinkId,
  loadingLabel,
  defaultCountry,
}: ILocationListing) => {
  const { fetchData, state } = useListingComponentData<
    ILocationListingRequest,
    ILocationListingResponse[]
  >(apiEndpoint, []);
  const [selectedCountry, setSelectedCountry] = useState<string>('');

  useEffect(() => {
    // Get country from the query string and set it as the selected country
    const parsedUrlParams = queryString.parse(
      queryString.pick(window?.location.search, ['country']),
      { arrayFormat: 'comma' },
    );
    const country = parsedUrlParams.country?.length
      ? parsedUrlParams.country.toString()
      : defaultCountry;

    if (!countryList.some(({ id }) => id === country)) return;

    setSelectedCountry(country);
  }, []);

  useEffect(() => {
    // Fetch data when the selected country changes
    if (!selectedCountry) return;

    fetchData({ country: selectedCountry, pageLinkId });
  }, [selectedCountry]);

  return (
    <section className="tw-component-margin">
      <div className="tw-mb-16 tw-bg-grey-10 tw-py-16 md:tw-mb-20 md:tw-py-20">
        <div className="b2b-container tw-flex tw-gap-4 max-md:tw-flex-col md:tw-gap-8">
          <div className="tw-px-2 md:tw-basis-1/2 xl:tw-basis-2/3">
            <Text variant="ds-headline-2" className=" tw-text-grey-100">
              {header}
            </Text>
            <Text variant="body" className=" tw-mt-4 tw-text-grey-80">
              {subHeader}
            </Text>
          </div>
          <div className="tw-px-2 max-md:tw-mt-6 md:tw-basis-1/2 xl:tw-basis-1/3">
            <Select
              displayLabel={false}
              label={dropdownLabel}
              name="country"
              options={countryList}
              placeholder={dropdownLabel}
              setValue={setSelectedCountry}
              selectedItem={
                countryList.find(
                  (country) => country.id === selectedCountry,
                ) ?? { id: '', label: dropdownLabel }
              }
              onSelectedItemChange={(selectedCountry) =>
                setSelectedCountry(selectedCountry?.id ?? defaultCountry)
              }
            />
          </div>
        </div>
      </div>
      <div className="b2b-container tw-min-h-80">
        {!state.loading && (
          <div className="tw-grid tw-grid-cols-1 tw-gap-y-8 tw-px-2 md:tw-grid-cols-2 md:tw-gap-x-8 md:tw-px-4 xl:tw-grid-cols-3">
            {state.data.map((result) => (
              <div key={result.companyName}>
                <ContactCard
                  title={result.companyName}
                  address={result.address}
                  poBox={result.visitingAddress}
                  emails={result.email}
                  phoneNumber={result.phone}
                  website={result.website}
                  tags={result.products}
                />
              </div>
            ))}
          </div>
        )}
        {state.loading && (
          <div className="tw-flex tw-justify-center tw-pt-40">
            <LoadingSpinner
              size={SizeEnum.Medium}
              loadingLabel={loadingLabel}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default LocationListing;
